import { useState, useEffect, useMemo } from "react";
import { addDays, fromUnixTime } from "date-fns";
import { NextPage } from "next";
import { Section } from "components/aud/base/Section";
import Head from "next/head";
import { PageTitle } from "components/aud/ui/PageTitle";
import classnames from "classnames";
import { ClassroomsWrapper } from "~/components/aud/ui/ClassroomsWrapper";
import css from "./index.module.scss";
import { useUser } from "context/userContext";
import { useRouter } from "next/router";
import { NowdoAgainModal } from "~/components/aud/ui/NowdoAgainModal";
import { CreateClassroomModal } from "~/components/aud/ui/CreateClassroomModal";
import { useGetClassroomQuery, useListClassroomsQuery, useListCategoriesQuery } from "~/hooks/rtk-query-api";
import { SignUpModal } from "~/components/aud/ui/SignUpModeal";
import { Category, Classroom } from "~/generated/rtk-query/nowdo-live-api.generated";
import { getInitialPageProps } from "~/store";
import { loadClassroomsPageData } from "~/components/links/ClassroomsPageLink";
import { redirect } from "~/submodules/redirect";
import { splitArray } from "~/submodules/splitArray";
import { CategorizedItems, categorizeItems } from "~/submodules/categorizeItems";

const categorizeClassrooms = (allClassrooms: Classroom[], categories: Category[]) => {
  const [pickupClassrooms, classrooms] = splitArray(allClassrooms, (classroom) => classroom.pickup);

  return {
    pickupClassrooms,
    categorizedClassrooms: categorizeItems(categories, classrooms),
  };
};

type NowDoAgainProps = {
  classroomId: string;
  onClose: () => unknown;
};

const NowDoAgain: React.FC<NowDoAgainProps> = ({ classroomId, onClose }) => {
  const { user } = useUser();
  const [isOpenCreateClassroomModal, setIsOpenCreateClassroomModal] = useState(false);
  const { data, isSuccess } = useGetClassroomQuery(
    {
      id: classroomId,
    },
    { refetchOnMountOrArgChange: true }
  );

  if (!isSuccess || !user) {
    return null;
  }
  const { classroom } = data;

  if (isOpenCreateClassroomModal) {
    return (
      <CreateClassroomModal
        open={isOpenCreateClassroomModal}
        user={user}
        onClose={() => setIsOpenCreateClassroomModal(false)}
        onNo={onClose}
        defaultValues={{
          name: classroom.name,
          description: classroom.description,
          categoryId: classroom.category.id,
          allUsers: classroom.allUsers,
          audienceMode: classroom.audienceMode,
          startAt: addDays(fromUnixTime(classroom.startAt), 1),
        }}
      />
    );
  }

  return <NowdoAgainModal title={classroom.name} onOk={() => setIsOpenCreateClassroomModal(true)} onNo={onClose} />;
};

const ClassroomsPage: NextPage = () => {
  const router = useRouter();
  const { user } = useUser();

  const classrooms = useListClassroomsQuery({});
  const categories = useListCategoriesQuery();
  const type = router.query.type as string;
  const classroomId = router.query.id as string;
  const [openSignUpModel, setOpenSignUpModal] = useState(type === "signup");
  const [openAgainModel, setOpenAgainModal] = useState(type === "again");

  const { pickupClassrooms, categorizedClassrooms } = useMemo((): {
    pickupClassrooms: Classroom[];
    categorizedClassrooms: CategorizedItems<Classroom>[];
  } => {
    if (classrooms.isError || categories.isError || !classrooms.data || !categories.data) {
      return {
        pickupClassrooms: [],
        categorizedClassrooms: [],
      };
    }
    const { pickupClassrooms, categorizedClassrooms } = categorizeClassrooms(
      classrooms.data.classrooms,
      categories.data.categories
    );
    return {
      pickupClassrooms,
      categorizedClassrooms,
    };
  }, [classrooms.data, categories.data]);

  useEffect(() => {
    if (user?.under12Student) {
      router.push("/classroom_unavailable");
    }
  }, [user]);

  return (
    <main>
      <Head>
        <title>クラスルーム一覧</title>
      </Head>
      <Section>
        <div className={classnames([css.maxWidth, css.pageTitle])}>
          <PageTitle title="Classroom" sub="クラスルーム一覧" />
        </div>
        <div>
          {pickupClassrooms.length > 0 && <ClassroomsWrapper title="ピックアップ" classrooms={pickupClassrooms} />}
          {categorizedClassrooms.map((category) => (
            <ClassroomsWrapper key={category.categoryId} title={category.categoryName} classrooms={category.items} />
          ))}
        </div>
        <div>{openAgainModel && <NowDoAgain classroomId={classroomId} onClose={() => setOpenAgainModal(false)} />}</div>
        <div>
          {!user && (
            <SignUpModal open={openSignUpModel} onClose={() => setOpenSignUpModal(false)} onSuccess={() => {}} />
          )}
        </div>
      </Section>
    </main>
  );
};

ClassroomsPage.getInitialProps = getInitialPageProps((store) => async (context) => {
  const { query } = context;
  const type = query.type;
  const classroomId = query.id;

  if (needRedirect(classroomId, type)) {
    const location = `/classrooms/${classroomId}`;
    redirect(context, location);
    return {};
  }

  await loadClassroomsPageData(store, classroomId, type);
  return {};
});

function needRedirect(maybeClassroomId: unknown, maybeType: unknown) {
  return typeof maybeClassroomId === "string" && typeof maybeType !== "string";
}

export default ClassroomsPage;
